/**
 * Invoice detail — info + ações (approve / advance / mark paid).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Printer, Receipt, TrendingUp, Wallet } from "lucide-react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { invoicePrintUrl } from "@/lib/api-system";

import { DetailCard, DetailLayout, KeyValue } from "@/components/detail-layout";
import { InvoiceStatusPill } from "@/components/status-pill";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { invoicesApi } from "@/lib/api-jobs";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";

export function InvoiceDetailPage() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const inv = useQuery({ queryKey: ["invoice", id], queryFn: () => invoicesApi.get(id), enabled: !!id });

  const mutate = (fn: (id: string) => Promise<unknown>, label: string) =>
    useMutation({
      mutationFn: () => fn(id),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["invoice", id] });
        qc.invalidateQueries({ queryKey: ["invoices"] });
        toast.success(label);
      },
      onError: (e: Error) => toast.error("Falha", { description: e.message }),
    });

  const approve = mutate(invoicesApi.approve, "Fatura aprovada");
  const requestAdvance = mutate(invoicesApi.requestAdvance, "Antecipação solicitada");
  const approveAdvance = mutate(invoicesApi.approveAdvance, "Antecipação aprovada");
  const markPaid = mutate(invoicesApi.markPaid, "Marcada como paga");

  if (!inv.data) return <div className="text-muted-foreground py-10">Carregando…</div>;
  const i = inv.data;

  const canApprove = i.status === "awaiting_nf" || i.status === "reviewing";
  const canRequestAdvance = i.status === "scheduled" && !i.advance_requested;
  const canApproveAdvance = i.advance_requested && !i.advance_processed;
  const canMarkPaid = i.status === "scheduled" || i.status === "reviewing";

  return (
    <DetailLayout
      backTo="/invoices"
      eyebrow={`Fatura ${i.code}`}
      visual={
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10">
          <Receipt className="h-6 w-6" />
        </div>
      }
      title={formatCurrency(i.total)}
      subtitle={`Programação: ${formatDate(i.scheduled_payment_date)}`}
      badges={
        <>
          <InvoiceStatusPill status={i.status} />
          <Badge variant="default" className="capitalize">{i.contract_type}</Badge>
          {i.advance_processed && <Badge variant="success">Antecipada (Loopi)</Badge>}
        </>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.open(invoicePrintUrl(id), "_blank")}>
            <Printer className="h-4 w-4" /> Imprimir / PDF
          </Button>
          {canApprove && (
            <Button variant="outline" onClick={() => approve.mutate()} disabled={approve.isPending}>
              <Check className="h-4 w-4" /> Aprovar
            </Button>
          )}
          {canRequestAdvance && (
            <Button variant="outline" onClick={() => requestAdvance.mutate()} disabled={requestAdvance.isPending}>
              <TrendingUp className="h-4 w-4" /> Solicitar antecipação
            </Button>
          )}
          {canApproveAdvance && (
            <Button onClick={() => approveAdvance.mutate()} disabled={approveAdvance.isPending}>
              <TrendingUp className="h-4 w-4" /> Aprovar antecipação
            </Button>
          )}
          {canMarkPaid && (
            <Button onClick={() => markPaid.mutate()} disabled={markPaid.isPending}>
              <Wallet className="h-4 w-4" /> Marcar como pago
            </Button>
          )}
        </div>
      }
    >
      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info">Detalhes</TabsTrigger>
          <TabsTrigger value="finance">Financeiro</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DetailCard title="Identificação">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <KeyValue label="Código" value={<span className="font-mono">{i.code}</span>} />
                <KeyValue label="Tipo contrato" value={i.contract_type} />
                <KeyValue label="Status" value={<InvoiceStatusPill status={i.status} />} />
                <KeyValue label="Validada" value={i.nf_validated ? "Sim" : "Não"} />
              </div>
            </DetailCard>
            <DetailCard title="Datas">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <KeyValue label="Data fatura" value={formatDate(i.invoice_date)} />
                <KeyValue label="Pagamento" value={formatDate(i.scheduled_payment_date)} />
                <KeyValue label="Pago em" value={formatDateTime(i.paid_at)} />
                <KeyValue label="Criada" value={formatDateTime(i.created_at)} />
              </div>
            </DetailCard>
            {i.notes && (
              <DetailCard title="Observações" className="md:col-span-2">
                <p className="text-sm whitespace-pre-wrap">{i.notes}</p>
              </DetailCard>
            )}
          </div>
        </TabsContent>

        <TabsContent value="finance">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <DetailCard title="Serviços">
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(i.services_total)}</p>
            </DetailCard>
            <DetailCard title="Reembolsos">
              <p className="text-2xl font-bold tabular-nums">{formatCurrency(i.reimbursement_total)}</p>
            </DetailCard>
            <DetailCard title="Total" className="bg-primary-50/30 dark:bg-primary-500/5">
              <p className="text-2xl font-bold tabular-nums text-primary-700 dark:text-primary-300">
                {formatCurrency(i.total)}
              </p>
            </DetailCard>
          </div>
          {(i.advance_requested || i.advance_processed) && (
            <DetailCard title="Antecipação Loopi">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3">
                <KeyValue label="Solicitada" value={i.advance_requested ? "Sim" : "Não"} />
                <KeyValue label="Processada" value={i.advance_processed ? "Sim" : "Não"} />
                <KeyValue label="Taxa" value={i.advance_fee_amount ? formatCurrency(i.advance_fee_amount) : "—"} />
                <KeyValue label="Líquido" value={i.advance_fee_amount ? formatCurrency(i.total - i.advance_fee_amount) : formatCurrency(i.total)} />
              </div>
            </DetailCard>
          )}
        </TabsContent>
      </Tabs>
    </DetailLayout>
  );
}
